import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { SocialSharing } from '@awesome-cordova-plugins/social-sharing/ngx';
import { Clipboard } from '@capacitor/clipboard';
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from '@capacitor/filesystem';
import { IonIcon, ModalController, NavController, IonHeader, IonToolbar, IonButton, IonTitle, IonContent } from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { DisplayService } from "@qid/core";
import { Environment, ENVIRONMENT } from "@qid/env";
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent, SuperInputComponent } from '@qid/super';
import { QRCodeComponent } from 'angularx-qrcode';
import * as htmltoimage from 'html-to-image';
import { firstValueFrom } from 'rxjs';
import { AppName } from '../../../../enum/app-name.enum';
import { ScannersFacade } from "../../+state/scanners.facade";
import { AuthSelectors } from '../../../authentication';
import { CollectionTemplate } from "../../../collection-template";
import { UpdateScannerDto } from "../../dto/update-scanner.dto";
import { Scanner } from "../../models/scanner";
import { AddTemplateOptionComponent } from "../add-template-option/add-template-option.component";

@Component({
  selector: 'view-scanner',
  templateUrl: './view-scanner.component.html',
  styleUrls: ['./view-scanner.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SexyModule,
    QRCodeComponent,
    SuperButtonComponent,
    SuperInputComponent,
    IonIcon,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonContent
  ],
  providers: [
    SocialSharing
  ]
})
export class ViewScannerComponent implements OnInit {

  @Input() scanner: Scanner
  @Input() loading: boolean = false;
  @Input() closeButton: boolean = false;
  @Output() closed: EventEmitter<any> = new EventEmitter<any>();

  templates: CollectionTemplate[] = [];

  @ViewChild('qrwrapper') qrwrapper: any;

  baseUrl: string = this.env.collectUrl;

  public orgId$ = firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId))

  constructor(
    private modalController: ModalController,
    private displayService: DisplayService,
    private socialSharing: SocialSharing,
    private scannersFacade: ScannersFacade,
    private navCtrl: NavController,
    private router: Router,
    private store: Store,
    @Inject(ENVIRONMENT) private env: Environment
  ) {
  }

  ngOnInit() {
  }

  close() {
    if (!this.closeButton) {
      return this.modalController.dismiss();
    }
    this.closed.emit();
  }

  share = async () => {

    const dataUrl = await this.getQRImage()

    const text =
      'Quickly share your IDs with ' +
      this.scanner.org?.name +
      ' for ' +
      this.scanner.optionsTitle;

    if (Capacitor.getPlatform() == 'android') {
      return this.socialSharing.share(text, 'www.oneqid.com', dataUrl);
    }
    if (Capacitor.getPlatform() == 'ios') {
      return this.socialSharing.shareViaWhatsApp(text, dataUrl, this.baseUrl + this.scanner.qr);
    }

    if (Capacitor.getPlatform() == 'web') {
      return this.socialSharing.shareViaWhatsApp(text, dataUrl, this.baseUrl + this.scanner.qr);
    }

  }

  getTitle() {
    return this.scanner.scannerTemplateOptions.length > 1 ? this.scanner.commonTitle : this.scanner.scannerTemplateOptions[0].template.purpose
  }

  shareToLocal = async () => {

    const title = this.getTitle()

    if (!Capacitor.isNativePlatform()) {
      return this.saveOnDesktop()
    }

    if (Capacitor.getPlatform() == 'ios') {
      await this.socialSharing.saveToPhotoAlbum(await this.getQRImage())
      return this.displayService.toast({ message: "QR Saved" })
    }

    await this.saveOnPhone(await this.getQRImage(), title + "_qid_qr.jpeg");
    await this.displayService.toast({ message: 'QR Saved Successfully. You can find it in your device storage' });

  };

  removeTemplateOption = async (index: number) => {

    let scanner = JSON.parse(JSON.stringify(this.scanner))
    scanner?.scannerTemplateOptions.splice(index, 1)
    this.scanner = scanner
    await this.updateScanner();
  };

  async saveOnDesktop() {
    let link = document.createElement("a");
    document.body.appendChild(link);
    link.setAttribute("href", await this.getQRImage());
    link.setAttribute("download", this.getTitle() + "_qid_qr.jpeg");
    link.click();
    link.remove();
    await this.displayService.toast({ message: 'QR Saved Successfully. You can find it in your device storage' });

  }

  async saveOnPhone(data, filename) {
    const permissionStatus = await Filesystem.checkPermissions();
    if (permissionStatus.publicStorage != 'granted') {
      await Filesystem.requestPermissions();
    }
    await Filesystem.writeFile({
      path: filename,
      data: data,
      directory: Directory.Documents,
    });
  }


  public getQRImage() {
    const node = this.qrwrapper.nativeElement;
    return htmltoimage.toJpeg(node);
  }


  private async updateScanner() {
    let dto = new UpdateScannerDto()
    dto.scannerTemplateOptions = this.scanner.scannerTemplateOptions
    await this.scannersFacade.updateRecord(this.scanner._id, dto)
  }

  updateScannerField = async (value?) => {
    this.scanner = { ...this.scanner, commonTitle: value };
    let dto = new UpdateScannerDto();
    dto.scannerTemplateOptions = this.scanner.scannerTemplateOptions;
    dto.commonTitle = this.scanner.commonTitle;
    await this.scannersFacade.updateRecord(this.scanner._id, dto);
  }

  delete = async () => {
    await this.scannersFacade.delete(this.scanner._id);
    await this.displayService.toast({ message: 'QR Deleted Successfully' });
    if (this.closeButton) {
      return this.closed.emit(1);
    }
    await this.modalController.dismiss({ deleted: true });
  };


  async viewTemplate(template: CollectionTemplate) {
    await this.close();
    
    if (this.env.appName === AppName.BusinessSuite) {
      return this.navCtrl.navigateForward('/menu/templates/' + template._id);
    } else if (this.env.appName === AppName.Mobile) {
      return this.router.navigate(['/tabs/templates'], { state: { template: template } });
    }
  }


  async addTemplateOptions() {
    const modal = await this.modalController.create({
      component: AddTemplateOptionComponent,
      componentProps: {
        scanner: { ...this.scanner },
      }
    })
    await modal.present()
    const res = await modal.onDidDismiss()
    if (res?.data?.scanner) {
      this.scanner = { ...res.data?.scanner }
    }
  }


  public async copy() {
    await Clipboard.write({
      url: this.baseUrl + this.scanner.qr,
      label: 'Copied',
    }).then(() => {
      this.displayService.toast({ message: 'Copied to clipboard' });
    });
  }

}
